import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import { Dictionary } from 'src/app/models/dictionary.model';
import {
  DictionaryAppDependencyDto,
  DictionaryDto,
} from '../../models/dto/dictionaryDto.model';
import { List } from '../../models/list.model';
import { ConfigService } from 'src/app/core/services/config-service';
import { EnvironmentType } from '../../models/enums/environment-type';
import { DictionaryTreeResponseDto } from '../../models/dto/dictionaryTreeResponseDto.model';

@Injectable()
export class DictionaryService {
  private activeDictionaryRequests = new Map<
    string,
    Observable<DictionaryDto>
  >();

  constructor(private http: HttpClient) {}

  configService = inject(ConfigService);

  public getDictionaries(params: {
    page?: number;
    pageSize?: number;
    sort?: string;
    sortDirection?: string;
    filters?: string[];
    dictionaryIds?: number[];
    dictionaryUUIDs?: string[];
    languageIds?: number[];
    env: EnvironmentType;
  }): Observable<List<DictionaryDto>> {
    let url =
      this.configService.readConfig().baseUrlInfrastructure + '/dictionaries';

    let query = '';
    if (params.pageSize) {
      query += `&PageSize=${params.pageSize}`;
    }
    if (params.page) {
      query += `&PageNumber=${params.page}`;
    }
    if (params.sort) {
      query += `&Sort=${params.sort}`;
    }
    if (params.sortDirection) {
      query += `&SortDirection=${params.sortDirection}`;
    }
    if (params.languageIds && params.languageIds.length > 0) {
      query += `&LanguageIds=${params.languageIds.join('&LanguageIds=')}`;
    }
    if (params.dictionaryIds && params.dictionaryIds.length > 0) {
      query += `&DictionaryIds=${params.dictionaryIds.join('&DictionaryIds=')}`;
    }
    if (params.dictionaryUUIDs && params.dictionaryUUIDs.length > 0) {
      query += `&DictionaryUUIDs=${params.dictionaryUUIDs.join(
        '&DictionaryUUIDs='
      )}`;
    }
    if (params.filters && params.filters.length > 0) {
      query += `&Filter=${params.filters.join('&Filter=')}`;
    }

    const env = params.env || EnvironmentType.Config;
    query += `&Env=${env}`;

    if (query.length > 0) {
      url += '?' + query;
    }

    return this.http.get<List<DictionaryDto>>(url);
  }

  public getDictionariesLight(params: {
    page?: number;
    pageSize?: number;
    sort?: string;
    sortDirection?: string;
    filters: string[];
    languageIds?: number[];
    env: EnvironmentType;
  }): Observable<List<DictionaryDto>> {
    let url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/dictionaries/light';

    let query = '';
    if (params.pageSize) {
      query += `&PageSize=${params.pageSize}`;
    }
    if (params.page) {
      query += `&PageNumber=${params.page}`;
    }
    if (params.sort) {
      query += `&Sort=${params.sort}`;
    }
    if (params.sortDirection) {
      query += `&SortDirection=${params.sortDirection}`;
    }
    if (params.languageIds && params.languageIds.length > 0) {
      query += `&LanguageIds=${params.languageIds.join('&LanguageIds=')}`;
    }
    if (params.filters && params.filters.length > 0) {
      query += `&Filter=${params.filters.join('&Filter=')}`;
    }
    const env = params.env || EnvironmentType.Config;
    query += `&Env=${env}`;

    if (query.length > 0) {
      url += '?' + query;
    }
    return this.http.get<List<DictionaryDto>>(url);
  }

  public delete(uuid: string, env: string): Observable<any> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      `/dictionaries/${uuid}/${env}`;
    return this.http.delete(url);
  }

  public getDictionary(
    id: string,
    env: EnvironmentType
  ): Observable<DictionaryDto> {
    const requestKey = `${id}_${env}`;
    if (this.activeDictionaryRequests.has(requestKey)) {
      return this.activeDictionaryRequests.get(requestKey)!;
    }

    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/dictionaries/dictionary/' +
      id +
      '/' +
      env;

    const newRequest$ = this.http.get<DictionaryDto>(url).pipe(
      shareReplay(1),
      finalize(() => {
        this.activeDictionaryRequests.delete(requestKey);
      })
    );

    this.activeDictionaryRequests.set(requestKey, newRequest$);
    return newRequest$;
  }

  public getAppDependencies(
    uuid: string,
    env: EnvironmentType
  ): Observable<DictionaryAppDependencyDto> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      '/dictionaries/app-dependencies/' +
      uuid +
      '/' +
      env;
    return this.http.get<DictionaryAppDependencyDto>(url);
  }

  public create(dictionary: Dictionary): Observable<string> {
    const createUrl =
      this.configService.readConfig().baseUrlInfrastructure + '/dictionaries';
    return this.http.post<string>(createUrl, dictionary);
  }

  updateDictionary(dictionary: Dictionary): Observable<Dictionary> {
    const updateUrl = `${
      this.configService.readConfig().baseUrlInfrastructure
    }/dictionaries`;
    return this.http.put<Dictionary>(updateUrl, dictionary);
  }

  publishDictionaryVersion(
    uuid: string,
    envFrom: EnvironmentType,
    envTo: EnvironmentType
  ): Observable<DictionaryDto> {
    const url =
      this.configService.readConfig().baseUrlInfrastructure +
      `/dictionaries/publish` +
      `?uuid=${uuid}&envFrom=${envFrom}&envTo=${envTo}`;
    return this.http.get<DictionaryDto>(url);
  }

  /**
   * NEW: Get complete dictionary tree in a single API call
   * This replaces the need for multiple recursive calls to load child dictionaries
   */
  public getDictionaryTree(
    dictionaryId: string,
    params: {
      env: EnvironmentType;
      languageId?: number;
      valueIdsToShow?: number[];
    }
  ): Observable<DictionaryTreeResponseDto> {
    const baseUrl = this.configService.readConfig().baseUrlInfrastructure;
    let url = `${baseUrl}/dictionaries/${dictionaryId}/tree`;

    let queryParams: string[] = [];
    queryParams.push(`environmentType=${params.env}`);

    if (params.languageId) {
      queryParams.push(`languageId=${params.languageId}`);
    }

    if (params.valueIdsToShow && params.valueIdsToShow.length > 0) {
      queryParams.push(`valueIdsToShow=${params.valueIdsToShow.join(',')}`);
    }

    if (queryParams.length > 0) {
      url += '?' + queryParams.join('&');
    }

    return this.http.get<DictionaryTreeResponseDto>(url);
  }
}

// Response interface for the app dictionaries endpoint
export interface AppDictionariesResponse {
  dictionaries: { [uuid: string]: DictionaryDto };
  analysisStats: {
    appUuid: string;
    sectionsAnalyzed: number;
    controlsAnalyzed: number;
    presentationControlsAnalyzed: number;
    initialDictionariesFound: number;
    totalLoaded: number;
    dependencyRounds: number;
    loadingTimeMs: number;
  };
  success: boolean;
  errors?: string[];
}
